import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AppProvider } from "./context/AppContext";
import Shell from "./components/Shell";
import { Loading, EmptyState } from "./components/common";
import { ShieldAlert } from "lucide-react";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import SuperAdminLogin from "./pages/SuperAdminLogin";
import Dashboard from "./pages/Dashboard";
import ProcessMapPage from "./pages/ProcessMapPage";
import Assessment from "./pages/Assessment";
import Variants from "./pages/Variants";
import Conformance from "./pages/Conformance";
import Performance from "./pages/Performance";
import Simulation from "./pages/Simulation";
import AIChat from "./pages/AIChat";
import Insights from "./pages/Insights";
import Recommendations from "./pages/Recommendations";
import Monitoring from "./pages/Monitoring";
import DataStudio from "./pages/DataStudio";
import UsersPage from "./pages/UsersPage";
import AuditLog from "./pages/AuditLog";
import SettingsPage from "./pages/SettingsPage";
import SuperAdmin from "./pages/SuperAdmin";

export const homeFor = (user) => (user?.role === "super_admin" ? "/app/super-admin" : "/app");

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen pm-app-bg flex items-center justify-center"><Loading label="Initialising workspace…" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function RequireRole({ min, children }) {
  const { can } = useAuth();
  if (!can(min)) return <EmptyState icon={ShieldAlert} title="Access restricted" sub="Your role does not have permission to view this page. Contact your administrator." />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={user ? <Navigate to={homeFor(user)} replace /> : <Login />} />
      <Route path="/_sup-admin" element={user?.role === "super_admin" ? <Navigate to="/app/super-admin" replace /> : <SuperAdminLogin />} />
      <Route path="/app" element={<Protected><Shell /></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="process-map" element={<ProcessMapPage />} />
        <Route path="assessment" element={<Assessment />} />
        <Route path="variants" element={<Variants />} />
        <Route path="conformance" element={<Conformance />} />
        <Route path="performance" element={<Performance />} />
        <Route path="simulation" element={<RequireRole min="analyst"><Simulation /></RequireRole>} />
        <Route path="ai-chat" element={<AIChat />} />
        <Route path="insights" element={<Insights />} />
        <Route path="recommendations" element={<Recommendations />} />
        <Route path="monitoring" element={<Monitoring />} />
        <Route path="data" element={<RequireRole min="connector_admin"><DataStudio /></RequireRole>} />
        <Route path="users" element={<RequireRole min="admin"><UsersPage /></RequireRole>} />
        <Route path="audit" element={<RequireRole min="admin"><AuditLog /></RequireRole>} />
        <Route path="settings" element={<RequireRole min="admin"><SettingsPage /></RequireRole>} />
        <Route path="super-admin" element={<RequireRole min="super_admin"><SuperAdmin /></RequireRole>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppProvider>
          <Toaster position="top-right" richColors closeButton />
          <AppRoutes />
        </AppProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
